import { FC, useCallback } from 'react';
import { toast } from 'react-toastify';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button, { isButtonDisabled } from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useConfirm from 'src/hooks/useConfirm';
import useClosePoApi from 'src/services/useClosePOApi';
import { useContextSelector } from 'use-context-selector';
import { PoRelatedDocumentsContext } from '../po_related_documents/PoRelatedDocumentsContext';
import ClosePoWizard from './ClosePoWizard';
import { InvoiceContext } from './InvoiceDetailsContext';
import usePoButtonState from './usePoButtonState';

interface ClosePoProps {}

const ClosePo: FC<ClosePoProps> = () => {
  const refreshPage = useContextSelector(InvoiceContext, (value) => value.refreshPage);
  const isInvoiceComplete = useContextSelector(InvoiceContext, (value) => value.isInvoiceComplete);
  const throttledInvoiceLineItems = useContextSelector(
    InvoiceContext,
    (value) => value.throttledInvoiceLineItems
  );
  const getMessages = useContextSelector(InvoiceContext, (value) => value.getMessages);
  const isInvoiceActionButtonDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceActionButtonDisabled
  );
  const poId = useContextSelector(InvoiceContext, (value) => value.invoice.poId);

  const poDetails = useContextSelector(PoRelatedDocumentsContext, (value) => value.poDetails);

  const { closePo, closePoCallLoading } = useClosePoApi();
  const { confirm } = useConfirm();

  const [closePoMessage, closePoButtonState] = usePoButtonState(
    poDetails?.triggerPreCheck,
    'Close'
  );

  const handleClosePO = useCallback(async () => {
    confirm({
      title: 'Confirm PO Close',
      content: <ClosePoWizard lineItems={throttledInvoiceLineItems} />,
      cancelButtonText: 'Cancel',
      cancelColor: 'info',
      confirmButtonText: 'Confirm and Close',
    })
      .then(async (isClosePoApproved) => {
        if (isClosePoApproved) {
          const response = await closePo({ poIds: [poId] });
          response?.data?.processedIds.length > 0 &&
            confirm({
              title: 'Close PO',
              content: (
                <p>
                  {
                    'PO successfully closed. Any variances calculated can be viewed under Reports -> View Variances.'
                  }
                </p>
              ),
              hasNoConfirmButton: true,
              cancelButtonText: 'OK',
            });
          refreshPage();
          response?.data?.unProcessed.length > 0 &&
            toast.error(response?.data?.unProcessed.map((el: any) => el?.errorMessage));
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }, [closePo, confirm, throttledInvoiceLineItems, poId, refreshPage]);
  return (
    <>
      {!isButtonDisabled(closePoButtonState) && !isInvoiceComplete ? (
        <></>
      ) : (
        <PopUp
          popupAlign={{ horizontal: 'center', vertical: 'top' }}
          content={
            <div className="m-1 max-w-[350px]">
              <PopUpContentWrapper
                type="Error"
                contents={getMessages(closePoButtonState, closePoMessage)}
              />
            </div>
          }
          show={isInvoiceActionButtonDisabled(closePoButtonState)}
        >
          <Button
            onClick={handleClosePO}
            color="secondary"
            buttonState={closePoButtonState}
            disabled={isInvoiceActionButtonDisabled(closePoButtonState)}
            loading={closePoCallLoading}
          >
            Close PO
          </Button>
        </PopUp>
      )}
    </>
  );
};

export default ClosePo;
